import { ChangeDetectionStrategy, Component, effect, model, OnInit, untracked, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { InputTextModule } from 'primeng/inputtext';
import { DocumentDirection, DocumentLineItem } from '../../../../generated/generated-types';
import { LineItemDataService } from '../../services/line-item-data.service';
import { ConversionResult, DliUnitConversionDropDownComponent } from './dli-unit-conversion-drop-down.component';

@Component({
  selector: 'app-formly-dli-unit-conversion-drop-down',
  templateUrl: './formly-dli-unit-conversion-drop-down.component.html',
  styleUrl: './formly-dli-unit-conversion-drop-down.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DliUnitConversionDropDownComponent,
    ReactiveFormsModule,
    FormlyModule,
    InputTextModule
  ]
})
export class FormlyDliUnitConversionDropDownComponent extends FieldType<FieldTypeConfig> implements OnInit {

  documentLineItem = model<DocumentLineItem>();
  value = model<ConversionResult>();
  baseUnitIdentifier = model<string>();
  documentDirection = model<DocumentDirection>();
  skipLoadUnitConversions = model<boolean>(false);
  focusedIndex = model<number>(0);
  minFractionDigits = model<number>();

  unitConversions = model<ConversionResult[]>([]);
  initializationDone = model<boolean>(false);

  constructor(
    private lineItemDataService: LineItemDataService
  ) {
    super();

    // Sync ConversionResult value to formControl as Unit (including the first user selection).
    effect(() => {
      const currentValue = this.value();
      if (!currentValue?.unit?.id || !this.initializationDone())
        return;
      untracked(() => {
        const unit = currentValue.unit;
        const currentFormValue = this.formControl?.value;
        if (unit?.id === currentFormValue?.id)
          return;

        this.formControl?.setValue(unit ?? null, { emitEvent: false });
        this.formState?.detailComponent?.form?.patchValue({ unit }, { emitEvent: false });
        if (this.formState?.detailComponent) {
          this.formState.detailComponent.activeUnitConversionValue.set(currentValue);
        }
      });
    });

    effect(() => {
      let unitConversionDetailComponentValue = this.formState?.detailComponent?.activeUnitConversionValue();
      if (unitConversionDetailComponentValue) {
        let valueInItems = (this.unitConversions() as ConversionResult[]).find(item => item?.unit?.id === unitConversionDetailComponentValue?.unit?.id);
        if (valueInItems) {
          untracked(() => {
            if (this.value()?.unit?.id !== valueInItems?.unit?.id) {
              this.unitConversions.set(this.unitConversions().filter(item => item.id !== 'dummy-data'));
              this.value.set(valueInItems);
            }
          });
        }
      }
    });


  }

  ngOnInit() {
    this.initializationDone.set(false);

    // Get documentLineItem from model or formState
    if (this.model && !this.documentLineItem()) {
      this.documentLineItem.set(this.model);
    }
    if (this.formState?.detailComponent?.activeUnitConversions) {
      this.unitConversions = this.formState.detailComponent.activeUnitConversions;
      let unitConversionDetailComponentValue = this.formState?.detailComponent?.activeUnitConversionValue();
      if (unitConversionDetailComponentValue) {
        let valueInItems = (this.unitConversions() as ConversionResult[]).find(item => item?.unit?.id === unitConversionDetailComponentValue?.unit?.id);
        if (valueInItems) {
          untracked(() => {
            this.value.set(valueInItems);
          });
        }
      }
    }

    if (this.unitConversions().length > 0) {
      this.skipLoadUnitConversions.set(true);
    }

    // Get documentDirection from props, model, or formState
    if (this.props?.documentDirection && !this.documentDirection()) {
      this.documentDirection.set(this.props.documentDirection);
    } else if (this.formState?.detailComponent?.document()?.documentDefinition?.documentDirection && !this.documentDirection()) {
      this.documentDirection.set(this.formState.detailComponent.document()?.documentDefinition?.documentDirection);
    }

    // Get other props from field props
    if (this.props?.baseUnitIdentifier !== undefined) {
      this.baseUnitIdentifier.set(this.props.baseUnitIdentifier);
    }
    if (this.props?.skipLoadUnitConversions !== undefined) {
      this.skipLoadUnitConversions.set(this.props.skipLoadUnitConversions);
    }
    if (this.props?.focusedIndex !== undefined) {
      this.focusedIndex.set(this.props.focusedIndex);
    }
    if (this.props?.minFractionDigits !== undefined) {
      this.minFractionDigits.set(this.props.minFractionDigits);
    }

    this.initializationDone.set(true);
  }
}
